import React, { useEffect, useMemo, useState } from "react";
import {
    Alert as MuiAlert,
    Button as MuiButton,
    Dialog as MuiDialog,
    DialogActions as MuiDialogActions,
    DialogContent as MuiDialogContent,
    DialogTitle as MuiDialogTitle,
    FormControl as MuiFormControl,
    InputLabel as MuiInputLabel,
    MenuItem as MuiMenuItem,
    Select as MuiSelect,
    type SelectChangeEvent
} from "@mui/material";
import type { ProviderId } from "@/api/integrations/links";
import type { ProviderState, SourceRow } from "./useIntegrationsData";

export interface ProductOption {
    projectid: number;
    projectname?: string;
}

export interface TrackerLinkInfo {
    providerId: ProviderId;
    providerLabel: string;
    externalName: string;
}

interface Props {
    open: boolean;
    state: ProviderState;
    products: ProductOption[];
    trackerLinkByProject: ReadonlyMap<number, TrackerLinkInfo>;
    gitLinkByProject: ReadonlyMap<number, TrackerLinkInfo>;
    linking: boolean;
    onConfirm: (row: SourceRow, projectId: number) => void;
    onCancel: () => void;
}

function sourceFieldLabel(state: ProviderState): string {
    return `${state.meta.name} ${state.meta.kind === "git" ? "Repository" : "Project"}`;
}

function sourceOptionLabel(row: SourceRow): string {
    return row.keyOrBranch
        ? `${row.externalName} · ${row.keyOrBranch}`
        : row.externalName;
}

export function LinkProductDialog({
    open,
    state,
    products,
    trackerLinkByProject,
    gitLinkByProject,
    linking,
    onConfirm,
    onCancel
}: Props) {
    const [projectId, setProjectId] = useState<number | "">("");
    const [externalRef, setExternalRef] = useState("");

    const linkableRows = useMemo(
        () => state.rows.filter(row => row.availableForLinking === true),
        [state.rows]
    );

    useEffect(() => {
        if (open) {
            setProjectId("");
            setExternalRef("");
        }
    }, [open, state.meta.id]);

    const unavailableReason = useMemo(() => {
        const reasons = new Map<number, string>();
        for (const product of products) {
            const existingLink = state.meta.kind === "tracker"
                ? trackerLinkByProject.get(product.projectid)
                : gitLinkByProject.get(product.projectid);
            if (existingLink) {
                const sourceKind = state.meta.kind === "git" ? "repository" : "project";
                reasons.set(
                    product.projectid,
                    `Already linked to ${existingLink.providerLabel} ${sourceKind} ${existingLink.externalName}`
                );
                continue;
            }
            const hasAvailableSource = linkableRows.some(row =>
                !row.links.some(link => link.projectId === product.projectid));
            if (!hasAvailableSource && linkableRows.length > 0) {
                reasons.set(product.projectid, `Already linked to every available ${state.meta.kind === "git" ? "repository" : "project"}`);
            }
        }
        return reasons;
    }, [gitLinkByProject, linkableRows, products, state.meta.kind, trackerLinkByProject]);

    const sourceOptions = useMemo(() => {
        if (projectId === "") return [];
        return linkableRows.filter(row =>
            !row.links.some(link => link.projectId === projectId));
    }, [linkableRows, projectId]);

    const selectedSource = sourceOptions.find(row => row.externalRef === externalRef);
    const sourceLabel = sourceFieldLabel(state);
    const sourcePlural = state.meta.kind === "git" ? "repositories" : "projects";

    const handleProductChange = (event: SelectChangeEvent<number | "">) => {
        const value = event.target.value;
        setProjectId(value === "" ? "" : Number(value));
        setExternalRef("");
    };

    return (
        <MuiDialog
            open={open}
            onClose={linking ? undefined : onCancel}
            aria-labelledby="link-product-title"
            PaperProps={{ className: "il-root", sx: { width: 460, borderRadius: "14px", p: 1 } }}
        >
            <MuiDialogTitle id="link-product-title" sx={{ fontSize: 17, fontWeight: 600, pb: 1 }}>
                Link product to {state.meta.name}
            </MuiDialogTitle>
            <MuiDialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
                <MuiFormControl fullWidth size="small">
                    <MuiInputLabel id="link-product-product-label">Product</MuiInputLabel>
                    <MuiSelect
                        labelId="link-product-product-label"
                        label="Product"
                        value={projectId}
                        disabled={linking}
                        onChange={handleProductChange}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                        <MuiMenuItem value="" disabled><em>Select a product</em></MuiMenuItem>
                        {products.map(product => {
                            const name = product.projectname || `Product ${product.projectid}`;
                            const reason = unavailableReason.get(product.projectid);
                            return (
                                <MuiMenuItem key={product.projectid} value={product.projectid} disabled={Boolean(reason)}>
                                    <span>{name}</span>
                                    {reason && <span className="il-select-reason">{reason}</span>}
                                </MuiMenuItem>
                            );
                        })}
                    </MuiSelect>
                </MuiFormControl>

                <MuiFormControl fullWidth size="small" disabled={linking || projectId === "" || sourceOptions.length === 0}>
                    <MuiInputLabel id="link-product-source-label">{sourceLabel}</MuiInputLabel>
                    <MuiSelect
                        labelId="link-product-source-label"
                        label={sourceLabel}
                        value={externalRef}
                        onChange={event => setExternalRef(event.target.value)}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                        <MuiMenuItem value="" disabled><em>{`Select a ${sourceLabel.toLowerCase()}`}</em></MuiMenuItem>
                        {sourceOptions.map(row => (
                            <MuiMenuItem key={row.externalRef} value={row.externalRef}>
                                {sourceOptionLabel(row)}
                            </MuiMenuItem>
                        ))}
                    </MuiSelect>
                </MuiFormControl>

                {state.listingError && (
                    <MuiAlert severity="warning">
                        {`Available ${sourcePlural} could not be refreshed. Try again from Manage.`}
                    </MuiAlert>
                )}
                {products.length === 0 && (
                    <MuiAlert severity="info">No products are available to link.</MuiAlert>
                )}
                {!state.listingError && linkableRows.length === 0 && (
                    <MuiAlert severity="info">{`No ${state.meta.name} ${sourcePlural} are available.`}</MuiAlert>
                )}
                {projectId !== "" && linkableRows.length > 0 && sourceOptions.length === 0 && (
                    <MuiAlert severity="info">{`This product is already linked to every available ${state.meta.kind === "git" ? "repository" : "project"}.`}</MuiAlert>
                )}
            </MuiDialogContent>
            <MuiDialogActions sx={{ px: 3, pb: 2, gap: 1.25 }}>
                <MuiButton variant="outlined" disabled={linking} onClick={onCancel}>Cancel</MuiButton>
                <MuiButton
                    variant="contained"
                    disabled={linking || projectId === "" || !selectedSource}
                    onClick={() => selectedSource && projectId !== "" && onConfirm(selectedSource, projectId)}
                    sx={{ bgcolor: "var(--primary)", textTransform: "none" }}
                >
                    {linking ? "Linking…" : "Link Product"}
                </MuiButton>
            </MuiDialogActions>
        </MuiDialog>
    );
}
